/**
 * The facts a production handoff starts from, gathered from the job: what was
 * sold option by option, the upgrades, the contract and the start date.
 *
 * Split from handoff.ts, which writes the email from these, when the two
 * outgrew one file. Pure, like it.
 */

import {
  colorOptionsFor,
  colorRuleFor,
  type ColorProduct,
} from "./colorRules.js";
import type { HandoffFacts } from "./handoff.js";
import { type Cents } from "./money.js";
import {
  chosenIds,
  documentUpgrades,
  estimateOptionLabel,
  isWordingLine,
  type PresentedDocument,
} from "./presentation.js";
import {
  invoiceAmount,
  type SignedContract,
  type SoldOption,
} from "./quickbooks.js";
import { colourOf } from "./selections.js";
import { recordUpgrades } from "./signedDocument.js";
import type { EstimateItem } from "./types.js";

/**
 * What was sold, option by option, as production reads it.
 *
 * Only the signed options — a job that priced three roofs and sold one hands
 * off the one — and nothing when nothing was e-signed: the job cannot tell
 * which estimate a paper contract bought, so the rep types it (Jonathon,
 * 2026-10-11, ADR 0280). Each option lists its priced
 * lines in the builder's order: the proposal wording and the optional upgrade
 * blocks are left out, the first being copy and the second being the upgrades
 * section's business.
 */
export function handoffOptions(
  estimates: readonly {
    id: string;
    name?: string | null;
    version?: number | null;
    items: readonly (Pick<
      EstimateItem,
      | "description"
      | "color"
      | "section"
      | "work_doing"
      | "sort_order"
      | "upgrade_group"
    > &
      Partial<Pick<EstimateItem, "product_id">>)[];
  }[],
  signedEstimateIds: readonly string[],
  /** The products on the lines; given, each option carries its colour question. */
  products?: readonly ColorProduct[],
): HandoffFacts["options"] {
  const signed = new Set(signedEstimateIds);
  const sold = estimates.filter((estimate) => signed.has(estimate.id));
  return sold.map((estimate) => {
    const items = [...estimate.items].sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
    );
    const priced = items.filter(
      (item) =>
        !isWordingLine(item) &&
        item.work_doing !== false &&
        !(item.upgrade_group ?? "").trim() &&
        (item.description ?? "").trim() !== "",
    );
    return {
      name: estimateOptionLabel(estimate, items),
      lines: priced.map((item) => ({
        name: (item.description ?? "").trim().split("\n")[0]?.trim() ?? "",
        color: item.color ?? null,
      })),
      ...(products ? optionColour(priced, products) : {}),
    };
  });
}

/**
 * The colour an option was sold in and the colours it could have been. The
 * colour is the Overview's roof colour, so the question and the Selections
 * card agree. The choices are the roof's own product, the one whose colour
 * must be chosen, and failing that any line with a colour choice at all —
 * gutters in brown or black. "Color Match" only follows the roof and is never
 * a choice.
 */
function optionColour(
  lines: readonly (Pick<EstimateItem, "color" | "description"> & {
    product_id?: string | null;
  })[],
  products: readonly ColorProduct[],
): { colour: string | null; colourOptions: string[]; roof: boolean } {
  const byId = new Map(products.map((product) => [product.id, product]));
  const product = (line: { product_id?: string | null }) =>
    line.product_id ? byId.get(line.product_id) : undefined;
  const required = lines.filter(
    (line) => colorRuleFor(product(line)) === "required",
  );
  const choosable = required.length
    ? required
    : lines.filter((line) => {
        const rule = colorRuleFor(product(line));
        return rule !== null && rule !== "match";
      });
  return {
    colour: colourOf(
      lines.map((line) => ({
        name: line.description ?? "",
        color: line.color ?? null,
        rule: colorRuleFor(product(line)),
      })),
    ),
    colourOptions: [
      ...new Set(choosable.flatMap((line) => colorOptionsFor(product(line)))),
    ],
    roof: required.length > 0,
  };
}

/**
 * Which upgrades were bought and which were passed over, across every signed
 * document on the job.
 *
 * Read off the frozen documents, the same as the signed record is: an upgrade
 * the chosen package includes counts as bought, and one offered on a document
 * and bought on a later one is not listed as passed over.
 */
export function handoffUpgrades(
  signed: readonly {
    document: PresentedDocument;
    estimateIds: readonly string[];
    upgradeKeys: readonly string[];
  }[],
): { chosen: string[]; notChosen: string[] } {
  const chosen = new Set<string>();
  const offered = new Set<string>();
  for (const row of signed) {
    for (const upgrade of documentUpgrades(row.document)) {
      offered.add(upgrade.name);
    }
    for (const { upgrade } of recordUpgrades(
      row.document,
      chosenIds(row.estimateIds),
      row.upgradeKeys,
    )) {
      chosen.add(upgrade.name);
    }
  }
  return {
    chosen: [...chosen],
    notChosen: [...offered].filter((name) => !chosen.has(name)),
  };
}

/**
 * The contract the email lists: what the live signatures say the options they
 * bought are worth.
 *
 * `signed` is the signatures that still stand — a voided one has lost its
 * force and is left out before this. An option priced beside the one the
 * customer signed is not part of what production builds, so only the options
 * a signature names are totalled. Nothing e-signed is no figure at all: the
 * job cannot tell what a paper contract bought, so the rep types it (Jonathon,
 * 2026-10-11, ADR 0280).
 */
export function handoffContract(input: {
  signed: readonly SignedContract[];
  options: readonly SoldOption[];
}): Cents | null {
  if (input.signed.length === 0) return null;
  const signedIds = new Set(
    input.signed.flatMap((contract) => contract.estimateIds),
  );
  const amount = invoiceAmount({
    signed: input.signed,
    options: input.options.filter((option) => signedIds.has(option.estimateId)),
  });
  return amount > 0 ? amount : null;
}

/**
 * When the crew starts, for the email: the soonest booked work order on what
 * was sold, as the database gives it. Null when none is booked.
 */
export function handoffStart(
  starts: readonly (string | null | undefined)[],
): string | null {
  return (
    starts.filter((at): at is string => Boolean(at && at.trim())).sort()[0] ??
    null
  );
}
